/**
 * Website Builder - Create landing pages, multi-page sites, and bio pages
 * AI-powered content generation with easy customization
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Globe, FileText, Link2, Sparkles, ArrowRight, ArrowLeft,
  Check, Layout, Palette, Eye, Rocket, Plus, Trash2,
  Type, Image, Mail, Phone, MapPin, Users, Star,
  DollarSign, HelpCircle, BarChart3, Zap, Building2,
  Coffee, Utensils, Camera, Briefcase, Heart, ShoppingBag,
  Wrench, GraduationCap, X
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE_TYPES = [
  {
    id: 'landing_page',
    name: 'Landing Page',
    description: 'AI-generated one-page site with hero, about, services, and contact',
    icon: FileText,
    features: ['AI-written copy', 'Instant generation', 'Mobile responsive'],
    time: '30 seconds'
  },
  {
    id: 'bio_page',
    name: 'Bio / Link Page',
    description: 'Personal page with links, like Linktree but branded to you',
    icon: Link2,
    features: ['Social links', 'Custom buttons', 'Click analytics'],
    time: '1 minute'
  },
  {
    id: 'multi_page',
    name: 'Multi-Page Site',
    description: 'Full website with Home, About, Services, and Contact pages',
    icon: Layout,
    features: ['Multiple pages', 'Navigation menu', 'Section library'],
    time: '2 minutes'
  }
];

// Layout Templates for different business types
const LAYOUT_TEMPLATES = [
  {
    id: 'business',
    name: 'Business',
    icon: Building2,
    description: 'Professional business landing page',
    color: '#3b82f6',
    sections: ['hero', 'about', 'services', 'testimonials', 'contact'],
    style: 'modern'
  },
  {
    id: 'restaurant',
    name: 'Restaurant',
    icon: Utensils,
    description: 'Menu highlights, location, reservations',
    color: '#f59e0b',
    sections: ['hero', 'about', 'gallery', 'contact'],
    style: 'elegant'
  },
  {
    id: 'portfolio',
    name: 'Portfolio',
    icon: Camera,
    description: 'Showcase your work and skills',
    color: '#8b5cf6',
    sections: ['hero', 'about', 'gallery', 'services', 'contact'],
    style: 'minimal'
  },
  {
    id: 'agency',
    name: 'Agency',
    icon: Briefcase,
    description: 'Services, team, and case studies',
    color: '#14b8a6',
    sections: ['hero', 'services', 'features', 'team', 'testimonials', 'contact'],
    style: 'bold'
  },
  {
    id: 'nonprofit',
    name: 'Non-Profit',
    icon: Heart,
    description: 'Mission, impact, and donations',
    color: '#f43f5e',
    sections: ['hero', 'about', 'stats', 'testimonials', 'cta', 'contact'],
    style: 'modern'
  },
  {
    id: 'ecommerce',
    name: 'E-Commerce',
    icon: ShoppingBag,
    description: 'Products, features, and shopping',
    color: '#10b981',
    sections: ['hero', 'features', 'gallery', 'testimonials', 'pricing', 'contact'],
    style: 'modern'
  },
  {
    id: 'service',
    name: 'Service Pro',
    icon: Wrench,
    description: 'Local services and contractors',
    color: '#6366f1',
    sections: ['hero', 'services', 'about', 'testimonials', 'faq', 'contact'],
    style: 'bold'
  },
  {
    id: 'education',
    name: 'Education',
    icon: GraduationCap,
    description: 'Courses, programs, enrollment',
    color: '#0ea5e9',
    sections: ['hero', 'about', 'features', 'pricing', 'faq', 'contact'],
    style: 'modern'
  },
  {
    id: 'custom',
    name: 'Custom',
    icon: Sparkles,
    description: 'Build from scratch',
    color: '#a855f7',
    sections: ['hero', 'about', 'services', 'contact'],
    style: 'modern'
  }
];

const STYLE_OPTIONS = [
  { id: 'modern', name: 'Modern', description: 'Clean lines, subtle shadows' },
  { id: 'minimal', name: 'Minimal', description: 'Simple, no distractions' },
  { id: 'bold', name: 'Bold', description: 'Strong colors, big text' },
  { id: 'elegant', name: 'Elegant', description: 'Refined, sophisticated' }
];

const SECTION_ICONS = {
  hero: Zap,
  about: Users,
  services: Star,
  features: Check,
  testimonials: Star,
  pricing: DollarSign,
  contact: Mail,
  gallery: Image,
  faq: HelpCircle,
  cta: Rocket,
  team: Users,
  stats: BarChart3
};

export default function WebsiteBuilder() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [siteType, setSiteType] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [createdSite, setCreatedSite] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // Landing page form
  const [landingForm, setLandingForm] = useState({
    business_name: '',
    business_description: '',
    industry: '',
    target_audience: '',
    primary_color: '#14b8a6',
    style: 'modern',
    include_sections: ['hero', 'about', 'services', 'contact']
  });

  // Bio page form
  const [bioForm, setBioForm] = useState({
    name: '',
    title: '',
    bio: '',
    primary_color: '#14b8a6',
    links: [],
    social_links: []
  });

  // Multi-page form
  const [multiForm, setMultiForm] = useState({
    business_name: '',
    business_description: '',
    primary_color: '#14b8a6',
    style: 'modern',
    pages: ['home', 'about', 'services', 'contact']
  });

  const [newLink, setNewLink] = useState({ label: '', url: '' });

  const handleGenerate = async () => {
    if (!user || !token) {
      navigate('/auth');
      return;
    }

    setGenerating(true);
    try {
      let response;
      
      if (siteType === 'landing_page') {
        response = await axios.post(`${API}/website-builder/landing-page/generate`, landingForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (siteType === 'bio_page') {
        response = await axios.post(`${API}/website-builder/bio-page/create`, bioForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else if (siteType === 'multi_page') {
        response = await axios.post(`${API}/website-builder/multi-page/create`, multiForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setCreatedSite(response.data);
      setStep(4); // Success step
    } catch (err) {
      console.error('Failed to create site:', err);
      alert('Failed to create site. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const addLink = () => {
    if (newLink.label && newLink.url) {
      setBioForm({
        ...bioForm,
        links: [...bioForm.links, { ...newLink, icon: 'link' }]
      });
      setNewLink({ label: '', url: '' });
    }
  };

  const removeLink = (index) => {
    setBioForm({
      ...bioForm,
      links: bioForm.links.filter((_, i) => i !== index)
    });
  };

  const toggleSection = (section) => {
    const current = landingForm.include_sections;
    if (current.includes(section)) {
      setLandingForm({
        ...landingForm,
        include_sections: current.filter(s => s !== section)
      });
    } else {
      setLandingForm({
        ...landingForm,
        include_sections: [...current, section]
      });
    }
  };

  // Apply template settings
  const applyTemplate = (template) => {
    setSelectedTemplate(template);
    setLandingForm({
      ...landingForm,
      primary_color: template.color,
      style: template.style,
      include_sections: template.sections,
      industry: template.name
    });
  };

  // Generate preview data
  const generatePreview = () => {
    const previewSite = {
      business_name: siteType === 'bio_page' ? bioForm.name : landingForm.business_name,
      business_description: siteType === 'bio_page' ? bioForm.bio : landingForm.business_description,
      primary_color: siteType === 'bio_page' ? bioForm.primary_color : landingForm.primary_color,
      style: landingForm.style,
      type: siteType,
      sections: siteType === 'bio_page' ? [] : landingForm.include_sections.map((type, i) => ({
        id: `preview-${i}`,
        type,
        content: getPreviewContent(type, landingForm.business_name, landingForm.business_description)
      })),
      links: siteType === 'bio_page' ? bioForm.links : [],
      name: bioForm.name,
      title: bioForm.title,
      bio: bioForm.bio
    };
    setPreviewData(previewSite);
    setShowPreview(true);
  };

  // Generate preview content for sections
  const getPreviewContent = (type, businessName, description) => {
    switch (type) {
      case 'hero':
        return {
          headline: `Welcome to ${businessName || 'Your Business'}`,
          subheadline: description || 'Your trusted partner for quality services',
          cta_text: 'Get Started'
        };
      case 'about':
        return {
          title: 'About Us',
          content: description || 'We are dedicated to providing exceptional service and value to our customers.'
        };
      case 'services':
        return {
          title: 'Our Services',
          services: [
            { name: 'Service One', description: 'Description of your first service' },
            { name: 'Service Two', description: 'Description of your second service' },
            { name: 'Service Three', description: 'Description of your third service' }
          ]
        };
      case 'contact':
        return {
          title: 'Contact Us',
          cta_text: 'Send Message'
        };
      default:
        return { title: type.charAt(0).toUpperCase() + type.slice(1) };
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-950 pt-24 pb-16 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <Globe className="w-16 h-16 text-teal-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Website Builder</h2>
          <p className="text-zinc-400 mb-6">Sign in to create your AI-powered website</p>
          <Button onClick={() => navigate('/auth')} className="bg-teal-500 hover:bg-teal-600">
            Sign In to Start
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        
        {/* Progress */}
        {step < 4 && (
          <div className="mb-12">
            <div className="flex items-center justify-center gap-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    s < step ? 'bg-teal-500 text-white' :
                    s === step ? 'bg-teal-500/20 border-2 border-teal-500 text-teal-400' :
                    'bg-zinc-800 text-zinc-500'
                  }`}>
                    {s < step ? <Check className="w-5 h-5" /> : s}
                  </div>
                  {s < 3 && <div className={`w-16 h-0.5 mx-2 ${s < step ? 'bg-teal-500' : 'bg-zinc-800'}`} />}
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-12 mt-2 text-sm text-zinc-500">
              <span>Choose Type</span>
              <span>Details</span>
              <span>Customize</span>
            </div>
          </div>
        )}

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            {/* Step 1: Choose Type */}
            {step === 1 && (
              <div>
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">What do you want to create?</h1>
                  <p className="text-zinc-400">Choose the type of website that fits your needs</p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  {SITE_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = siteType === type.id;
                    
                    return (
                      <motion.button
                        key={type.id}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setSiteType(type.id);
                          setSelectedTemplate(null);
                        }}
                        className={`relative p-6 rounded-2xl text-left transition-all ${
                          isSelected 
                            ? 'bg-teal-500/20 border-2 border-teal-500' 
                            : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700'
                        }`}
                      >
                        {isSelected && (
                          <div className="absolute top-4 right-4">
                            <Check className="w-6 h-6 text-teal-400" />
                          </div>
                        )}
                        
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                          isSelected ? 'bg-teal-500/30' : 'bg-zinc-800'
                        }`}>
                          <Icon className={`w-6 h-6 ${isSelected ? 'text-teal-400' : 'text-zinc-400'}`} />
                        </div>
                        
                        <h3 className="text-lg font-bold text-white mb-2">{type.name}</h3>
                        <p className="text-zinc-400 text-sm mb-4">{type.description}</p>
                        
                        <div className="space-y-1 mb-4">
                          {type.features.map((feature, i) => (
                            <div key={i} className="flex items-center gap-2 text-xs text-zinc-500">
                              <Check className="w-3 h-3 text-teal-500" />
                              {feature}
                            </div>
                          ))}
                        </div>
                        
                        <span className="text-teal-400 text-sm">Ready in {type.time}</span>
                      </motion.button>
                    );
                  })}
                </div>

                {/* Layout Templates - Show only for landing pages */}
                {siteType === 'landing_page' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-10"
                  >
                    <h2 className="text-xl font-bold text-white mb-4">Choose a Template</h2>
                    <p className="text-zinc-400 text-sm mb-6">Start with a pre-designed layout for your industry</p>
                    
                    <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                      {LAYOUT_TEMPLATES.map((template) => {
                        const Icon = template.icon;
                        const isSelected = selectedTemplate?.id === template.id;
                        
                        return (
                          <motion.button
                            key={template.id}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => applyTemplate(template)}
                            className={`relative p-4 rounded-xl text-center transition-all ${
                              isSelected 
                                ? 'ring-2 ring-offset-2 ring-offset-zinc-950' 
                                : 'bg-zinc-900/50 border border-zinc-800 hover:border-zinc-600'
                            }`}
                            style={{ 
                              backgroundColor: isSelected ? `${template.color}20` : undefined,
                              borderColor: isSelected ? template.color : undefined,
                              ringColor: isSelected ? template.color : undefined
                            }}
                          >
                            <div 
                              className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2"
                              style={{ backgroundColor: `${template.color}30` }}
                            >
                              <Icon className="w-5 h-5" style={{ color: template.color }} />
                            </div>
                            <p className="text-white text-xs font-medium">{template.name}</p>
                            {isSelected && (
                              <div 
                                className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: template.color }}
                              >
                                <Check className="w-3 h-3 text-white" />
                              </div>
                            )}
                          </motion.button>
                        );
                      })}
                    </div>

                    {selectedTemplate && selectedTemplate.id !== 'custom' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800"
                      >
                        <p className="text-zinc-300 text-sm">
                          <strong style={{ color: selectedTemplate.color }}>{selectedTemplate.name}</strong>: {selectedTemplate.description}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {selectedTemplate.sections.map((section) => (
                            <span key={section} className="text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-400">
                              {section}
                            </span>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                )}

                <div className="flex justify-end mt-8">
                  <Button
                    onClick={() => setStep(2)}
                    disabled={!siteType}
                    className="bg-teal-500 hover:bg-teal-600 disabled:opacity-50"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Details */}
            {step === 2 && (
              <div className="max-w-xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Tell us about your business</h1>
                  <p className="text-zinc-400">AI will generate content based on this info</p>
                </div>

                {siteType === 'landing_page' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-zinc-300 mb-2">Business Name *</label>
                      <Input
                        value={landingForm.business_name}
                        onChange={(e) => setLandingForm({ ...landingForm, business_name: e.target.value })}
                        placeholder="e.g., Sarah's Bakery"
                        className="bg-zinc-900 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-300 mb-2">What does your business do? *</label>
                      <textarea
                        value={landingForm.business_description}
                        onChange={(e) => setLandingForm({ ...landingForm, business_description: e.target.value })}
                        placeholder="Describe your products/services, what makes you unique..."
                        rows={4}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-zinc-300 mb-2">Industry</label>
                        <Input
                          value={landingForm.industry}
                          onChange={(e) => setLandingForm({ ...landingForm, industry: e.target.value })}
                          placeholder="e.g., Food & Beverage"
                          className="bg-zinc-900 border-zinc-700 text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-zinc-300 mb-2">Target Audience</label>
                        <Input
                          value={landingForm.target_audience}
                          onChange={(e) => setLandingForm({ ...landingForm, target_audience: e.target.value })}
                          placeholder="e.g., Local families"
                          className="bg-zinc-900 border-zinc-700 text-white"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {siteType === 'bio_page' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-zinc-300 mb-2">Your Name *</label>
                      <Input
                        value={bioForm.name}
                        onChange={(e) => setBioForm({ ...bioForm, name: e.target.value })}
                        placeholder="Your name"
                        className="bg-zinc-900 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-300 mb-2">Title / Tagline *</label>
                      <Input
                        value={bioForm.title}
                        onChange={(e) => setBioForm({ ...bioForm, title: e.target.value })}
                        placeholder="e.g., Digital Creator | Coffee Lover"
                        className="bg-zinc-900 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-300 mb-2">Short Bio</label>
                      <textarea
                        value={bioForm.bio}
                        onChange={(e) => setBioForm({ ...bioForm, bio: e.target.value })}
                        placeholder="A brief description about yourself..."
                        rows={3}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                      />
                    </div>
                    
                    {/* Links */}
                    <div>
                      <label className="block text-zinc-300 mb-2">Links</label>
                      <div className="space-y-2 mb-3">
                        {bioForm.links.map((link, index) => (
                          <div key={index} className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3">
                            <span className="text-white flex-1">{link.label}</span>
                            <span className="text-zinc-500 text-sm truncate max-w-[150px]">{link.url}</span>
                            <button onClick={() => removeLink(index)} className="text-red-400 hover:text-red-300">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2">
                        <Input
                          value={newLink.label}
                          onChange={(e) => setNewLink({ ...newLink, label: e.target.value })}
                          placeholder="Label (e.g., My Website)"
                          className="bg-zinc-900 border-zinc-700 text-white flex-1"
                        />
                        <Input
                          value={newLink.url}
                          onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                          placeholder="URL"
                          className="bg-zinc-900 border-zinc-700 text-white flex-1"
                        />
                        <Button onClick={addLink} variant="outline" className="border-zinc-700">
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {siteType === 'multi_page' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-zinc-300 mb-2">Business Name *</label>
                      <Input
                        value={multiForm.business_name}
                        onChange={(e) => setMultiForm({ ...multiForm, business_name: e.target.value })}
                        placeholder="e.g., Acme Consulting"
                        className="bg-zinc-900 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-300 mb-2">Business Description *</label>
                      <textarea
                        value={multiForm.business_description}
                        onChange={(e) => setMultiForm({ ...multiForm, business_description: e.target.value })}
                        placeholder="What does your business do?"
                        rows={4}
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-4 py-3 text-white placeholder-zinc-500"
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-between mt-8">
                  <Button onClick={() => setStep(1)} variant="outline" className="border-zinc-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    disabled={
                      (siteType === 'landing_page' && (!landingForm.business_name || !landingForm.business_description)) ||
                      (siteType === 'bio_page' && (!bioForm.name || !bioForm.title)) ||
                      (siteType === 'multi_page' && (!multiForm.business_name || !multiForm.business_description))
                    }
                    className="bg-teal-500 hover:bg-teal-600 disabled:opacity-50"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Customize */}
            {step === 3 && (
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-8">
                  <h1 className="text-3xl font-bold text-white mb-2">Customize Your Site</h1>
                  <p className="text-zinc-400">Choose style and sections</p>
                </div>

                {/* Color Picker */}
                <div className="mb-8">
                  <label className="block text-zinc-300 mb-3">Brand Color</label>
                  <div className="flex items-center gap-4">
                    <input
                      type="color"
                      value={siteType === 'bio_page' ? bioForm.primary_color : landingForm.primary_color}
                      onChange={(e) => {
                        if (siteType === 'bio_page') {
                          setBioForm({ ...bioForm, primary_color: e.target.value });
                        } else if (siteType === 'landing_page') {
                          setLandingForm({ ...landingForm, primary_color: e.target.value });
                        } else {
                          setMultiForm({ ...multiForm, primary_color: e.target.value });
                        }
                      }}
                      className="w-16 h-16 rounded-lg cursor-pointer border-0"
                    />
                    <div className="flex gap-2">
                      {['#14b8a6', '#8b5cf6', '#f43f5e', '#f59e0b', '#3b82f6', '#10b981'].map(color => (
                        <button
                          key={color}
                          onClick={() => {
                            if (siteType === 'bio_page') {
                              setBioForm({ ...bioForm, primary_color: color });
                            } else if (siteType === 'landing_page') {
                              setLandingForm({ ...landingForm, primary_color: color });
                            } else {
                              setMultiForm({ ...multiForm, primary_color: color });
                            }
                          }}
                          className="w-10 h-10 rounded-lg border-2 border-transparent hover:border-white transition-colors"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Style Selection (for landing and multi-page) */}
                {siteType !== 'bio_page' && (
                  <div className="mb-8">
                    <label className="block text-zinc-300 mb-3">Style</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {STYLE_OPTIONS.map(style => {
                        const isSelected = (siteType === 'landing_page' ? landingForm.style : multiForm.style) === style.id;
                        return (
                          <button
                            key={style.id}
                            onClick={() => {
                              if (siteType === 'landing_page') {
                                setLandingForm({ ...landingForm, style: style.id });
                              } else {
                                setMultiForm({ ...multiForm, style: style.id });
                              }
                            }}
                            className={`p-4 rounded-xl text-left transition-all ${
                              isSelected
                                ? 'bg-teal-500/20 border-2 border-teal-500'
                                : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-600'
                            }`}
                          >
                            <span className="text-white font-medium block">{style.name}</span>
                            <span className="text-zinc-500 text-xs">{style.description}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Section Selection (for landing page) */}
                {siteType === 'landing_page' && (
                  <div className="mb-8">
                    <label className="block text-zinc-300 mb-3">Sections to Include</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {Object.entries(SECTION_ICONS).map(([section, Icon]) => {
                        const isSelected = landingForm.include_sections.includes(section);
                        return (
                          <button
                            key={section}
                            onClick={() => toggleSection(section)}
                            className={`p-3 rounded-lg flex items-center gap-3 transition-all ${
                              isSelected
                                ? 'bg-teal-500/20 border border-teal-500 text-teal-400'
                                : 'bg-zinc-800 border border-zinc-700 text-zinc-400 hover:border-zinc-600'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="capitalize text-sm">{section}</span>
                            {isSelected && <Check className="w-4 h-4 ml-auto" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex justify-between mt-8">
                  <Button onClick={() => setStep(2)} variant="outline" className="border-zinc-700">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <div className="flex gap-3">
                    <Button
                      onClick={generatePreview}
                      variant="outline"
                      className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                    <Button
                      onClick={handleGenerate}
                      disabled={generating}
                      className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
                    >
                      {generating ? (
                        <>
                          <Sparkles className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate My Site
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Success */}
            {step === 4 && createdSite && (
              <div className="max-w-xl mx-auto text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="w-24 h-24 bg-teal-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <Globe className="w-12 h-12 text-teal-400" />
                </motion.div>

                <h1 className="text-3xl font-bold text-white mb-2">Your Site is Ready!</h1>
                <p className="text-zinc-400 mb-8">{createdSite.message}</p>

                <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6 mb-8 text-left">
                  <h3 className="text-white font-semibold mb-4">What's Next:</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Eye className="w-3 h-3 text-teal-400" />
                      </div>
                      <div>
                        <p className="text-white">Preview your site</p>
                        <p className="text-zinc-500 text-sm">See how it looks before publishing</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Palette className="w-3 h-3 text-teal-400" />
                      </div>
                      <div>
                        <p className="text-white">Edit content</p>
                        <p className="text-zinc-500 text-sm">Customize text, images, and sections</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <Rocket className="w-3 h-3 text-teal-400" />
                      </div>
                      <div>
                        <p className="text-white">Publish</p>
                        <p className="text-zinc-500 text-sm">Make it live with a shareable link</p>
                      </div>
                    </li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={() => navigate('/my-websites')}
                    className="bg-teal-500 hover:bg-teal-600"
                  >
                    View My Sites
                  </Button>
                  <Button
                    onClick={() => {
                      setStep(1);
                      setSiteType('');
                      setCreatedSite(null);
                      setSelectedTemplate(null);
                    }}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Another
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {showPreview && previewData && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPreview(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-zinc-800">
                <div className="flex items-center gap-3">
                  <Eye className="w-5 h-5 text-teal-400" />
                  <span className="text-white font-semibold">Preview</span>
                  <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">
                    Draft - AI content will be generated
                  </span>
                </div>
                <button
                  onClick={() => setShowPreview(false)}
                  className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-zinc-400" />
                </button>
              </div>

              {/* Preview Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-130px)]">
                <PreviewRenderer site={previewData} />
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-between p-4 border-t border-zinc-800 bg-zinc-900">
                <p className="text-zinc-500 text-sm">
                  This is a preview. Actual content will be AI-generated.
                </p>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowPreview(false)}
                    variant="outline"
                    className="border-zinc-700"
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      setShowPreview(false);
                      handleGenerate();
                    }}
                    className="bg-gradient-to-r from-teal-500 to-cyan-500"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Now
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Preview Renderer Component
function PreviewRenderer({ site }) {
  const primaryColor = site.primary_color || '#14b8a6';

  if (site.type === 'bio_page') {
    return (
      <div 
        className="min-h-[500px] py-12 px-6"
        style={{ background: `linear-gradient(180deg, ${primaryColor}15 0%, #0a0a0a 50%)` }}
      >
        <div className="max-w-md mx-auto text-center">
          <div 
            className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl font-bold text-white"
            style={{ backgroundColor: primaryColor }}
          >
            {site.name?.charAt(0) || '?'}
          </div>
          <h1 className="text-2xl font-bold text-white">{site.name || 'Your Name'}</h1>
          <p className="text-zinc-400">{site.title || 'Your Title'}</p>
          {site.bio && <p className="text-zinc-300 mt-3 text-sm">{site.bio}</p>}

          <div className="space-y-3 mt-8">
            {site.links?.length > 0 ? site.links.map((link, i) => (
              <div
                key={i}
                className="block w-full py-4 px-6 rounded-xl text-center font-medium"
                style={{ backgroundColor: `${primaryColor}20`, border: `1px solid ${primaryColor}40`, color: 'white' }}
              >
                {link.label}
              </div>
            )) : (
              <div
                className="block w-full py-4 px-6 rounded-xl text-center font-medium"
                style={{ backgroundColor: `${primaryColor}20`, border: `1px solid ${primaryColor}40`, color: 'white' }}
              >
                Your Link Here
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Landing page preview
  return (
    <div className="min-h-[500px]" style={{ backgroundColor: '#0a0a0a' }}>
      {site.sections?.map((section, index) => (
        <PreviewSection 
          key={section.id || index} 
          section={section} 
          primaryColor={primaryColor}
          businessName={site.business_name}
        />
      ))}
    </div>
  );
}

function PreviewSection({ section, primaryColor, businessName }) {
  const content = section.content || {};

  switch (section.type) {
    case 'hero':
      return (
        <section className="py-20 px-6 relative">
          <div 
            className="absolute inset-0 opacity-20"
            style={{ background: `linear-gradient(135deg, ${primaryColor}40 0%, transparent 50%)` }}
          />
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              {content.headline || `Welcome to ${businessName || 'Your Business'}`}
            </h1>
            <p className="text-xl text-zinc-300 mb-6">
              {content.subheadline || 'Your tagline goes here'}
            </p>
            <button
              className="px-8 py-3 rounded-full text-white font-semibold"
              style={{ backgroundColor: primaryColor }}
            >
              {content.cta_text || 'Get Started'}
            </button>
          </div>
        </section>
      );

    case 'about':
      return (
        <section className="py-16 px-6 bg-zinc-900/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-4 text-center">
              {content.title || 'About Us'}
            </h2>
            <p className="text-zinc-300 text-center">
              {content.content || 'Your about content will be AI-generated based on your business description.'}
            </p>
          </div>
        </section>
      );

    case 'services':
      return (
        <section className="py-16 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">
              {content.title || 'Our Services'}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {(content.services || [{}, {}, {}]).map((service, i) => (
                <div key={i} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                    style={{ backgroundColor: `${primaryColor}20` }}
                  >
                    <Star className="w-5 h-5" style={{ color: primaryColor }} />
                  </div>
                  <h3 className="text-white font-semibold mb-2">
                    {service.name || `Service ${i + 1}`}
                  </h3>
                  <p className="text-zinc-400 text-sm">
                    {service.description || 'Service description will be generated'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );

    case 'contact':
      return (
        <section className="py-16 px-6 bg-zinc-900/50">
          <div className="max-w-md mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              {content.title || 'Contact Us'}
            </h2>
            <div className="space-y-4">
              <input 
                placeholder="Your Name" 
                className="w-full px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500"
                readOnly
              />
              <input 
                placeholder="Your Email" 
                className="w-full px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500"
                readOnly
              />
              <textarea 
                placeholder="Your Message" 
                rows={3}
                className="w-full px-4 py-3 rounded-lg bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 resize-none"
                readOnly
              />
              <button
                className="w-full py-3 rounded-lg text-white font-semibold"
                style={{ backgroundColor: primaryColor }}
              >
                {content.cta_text || 'Send Message'}
              </button>
            </div>
          </div>
        </section>
      );

    default:
      return (
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-white mb-4 capitalize">{section.type}</h2>
            <p className="text-zinc-400">This section will be AI-generated</p>
          </div>
        </section>
      );
  }
}
